// Decision history for an invoice (app.md §8) — one line per decision:
// "Import Control — Approved by James Irwin on June 10, 2026 at 12:14 AM",
// with the comment (essential for Denial Review) quoted after a colon, and the
// reason category the reviewer picked (reason_category) as a badge after it.
// The left border goes red on negative outcomes.
//
// PORT NOTE: every row here changed the invoice. A refused decision writes
// NOTHING now — the API rejects it and returns the reason to the caller — so
// the "Not applied" badge, the per-step decision-stamp inference behind it and
// the decision's rejection_reason column are all gone. If it is in this list,
// it happened.
import { Badge, Body1, makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import type { Decision } from '../lib/types'
import { reasonCategoryLabel } from '../lib/constants'

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '8px' },
  none: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  item: {
    borderLeft: `3px solid ${tokens.colorNeutralStroke2}`,
    paddingLeft: '12px',
  },
  itemDenied: { borderLeftColor: tokens.colorPaletteRedBorderActive },
  step: { fontWeight: tokens.fontWeightSemibold },
  reason: { whiteSpace: 'pre-wrap' },
  categoryBadge: { marginLeft: '8px', verticalAlign: 'middle' },
})

function outcomeIsNegative(decision: string | undefined): boolean {
  return decision === 'denied' || decision === 'archived without payment' || decision === 'archived for fresh invoice'
}

/** "Import Control Decision" → "Import Control". */
function stepLabel(step: string | undefined): string {
  return step ? step.replace(/ Decision$/, '') : 'Decision'
}

/** "approved" → "Approved". */
function outcomeLabel(decision: string | undefined): string {
  if (!decision) return 'Decided'
  return decision.charAt(0).toUpperCase() + decision.slice(1)
}

/** "June 10, 2026 at 12:14 AM". */
function whenLabel(value: string | undefined): string {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const date = d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  return `${date} at ${time}`
}

export function DecisionHistory({ decisions }: { decisions: Decision[] | 'loading' | undefined }) {
  const s = useStyles()
  // `block`: both are the whole return value of this component, mounted right
  // after a sibling <Subtitle2> heading — without it Body1 renders inline and
  // runs on the same line as the heading (item 1c, C4, 2026-08-13).
  if (decisions === undefined || decisions === 'loading') {
    return <Body1 block className={s.none}>Loading history…</Body1>
  }
  if (decisions.length === 0) {
    return <Body1 block className={s.none}>No decisions recorded yet.</Body1>
  }
  return (
    <div className={s.root}>
      {decisions.map((d) => {
        const negative = outcomeIsNegative(d.decision)
        const when = whenLabel(d.decided_at)
        const category = reasonCategoryLabel(d.reason_category)
        return (
          <Body1 key={d.id} block className={mergeClasses(s.item, negative && s.itemDenied)}>
            <span className={s.step}>{stepLabel(d.step)}</span>
            {' — '}
            {outcomeLabel(d.decision)} by {d.decided_by_name ?? d.decided_by_email ?? 'Unknown'}
            {when && ` on ${when}`}
            {d.reason && (
              <>
                : <span className={s.reason}>“{d.reason}”</span>
              </>
            )}
            {/* The reason category the reviewer picked alongside the comment
                (reason_category) — captured at every deny/archive. */}
            {category && (
              <Badge appearance="tint" color="informative" className={s.categoryBadge}>
                {category}
              </Badge>
            )}
          </Body1>
        )
      })}
    </div>
  )
}
